import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { scoreLabel } from "@/lib/constants";

export default function DashboardOwner() {
  const { user } = useAuth();
  const [tab, setTab] = useState("listings");
  const [listings, setListings] = useState([]);
  const [leads, setLeads] = useState([]);

  useEffect(() => {
    api.get("/my/listings").then((r) => setListings(r.data)).catch(() => {});
    api.get("/my/leads").then((r) => setLeads(r.data)).catch(() => {});
  }, []);

  const stats = {
    total: listings.length,
    published: listings.filter((l) => l.status === "published").length,
    pending: listings.filter((l) => l.status === "pending").length,
    leads: leads.length,
  };

  return (
    <div className="px-6 md:px-12 lg:px-16 py-12">
      <div className="mb-8">
        <div className="label-mono mb-2 text-amber-500">§ dashboard</div>
        <h1 className="font-display text-4xl md:text-5xl">Bonjour {user?.name}</h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-zinc-800 border border-zinc-800 mb-12">
        {[
          ["Annonces", stats.total],
          ["Publiées", stats.published],
          ["En validation", stats.pending],
          ["Leads reçus", stats.leads],
        ].map(([k, v]) => (
          <div key={k} className="bg-zinc-950 p-6">
            <div className="label-mono mb-2">{k}</div>
            <div className="font-display text-4xl font-mono-tech">{v}</div>
          </div>
        ))}
      </div>

      <div className="flex gap-6 border-b border-zinc-800 mb-6">
        {["listings", "leads"].map((t) => (
          <button
            key={t}
            data-testid={`tab-${t}`}
            onClick={() => setTab(t)}
            className={`pb-3 text-sm uppercase tracking-widest transition-colors ${tab === t ? "border-b-2 border-amber-500 text-amber-500" : "text-zinc-500 hover:text-zinc-200"}`}
          >
            {t === "listings" ? "Mes annonces" : "Demandes reçues"}
          </button>
        ))}
        <Link to="/deposer" className="ml-auto pb-3 text-sm uppercase tracking-widest text-amber-500">+ Nouvelle annonce</Link>
      </div>

      {tab === "listings" && (
        <>
          {listings.length === 0 ? (
            <div className="border border-zinc-800 p-16 text-center">
              <div className="label-mono mb-4">Aucune annonce pour l'instant</div>
              <Link to="/deposer" className="inline-block bg-amber-500 text-black px-6 py-3 text-xs uppercase tracking-widest hover:bg-amber-400">
                Déposer une annonce
              </Link>
            </div>
          ) : (
            <div className="border border-zinc-800 divide-y divide-zinc-800">
              {listings.map((l) => {
                const s = scoreLabel(l.score || 0);
                return (
                  <Link to={`/annonces/${l.id}`} key={l.id} className="p-4 flex items-center gap-4 hover:bg-zinc-900 transition-colors" data-testid={`my-listing-${l.id}`}>
                    <div className="w-20 aspect-[4/3] bg-zinc-900 flex-shrink-0">
                      {l.photos?.[0] && <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${l.photos[0]}`} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1">
                      <div className="font-display text-lg">{l.title}</div>
                      <div className="label-mono">{l.city} · {l.surface_exploitable || l.surface_total}m²</div>
                    </div>
                    <div className={`font-mono-tech text-sm ${s.color}`}>[ {l.score || 0} / 100 ]</div>
                    <StatusBadge status={l.status} />
                  </Link>
                );
              })}
            </div>
          )}
        </>
      )}

      {tab === "leads" && (
        <>
          {leads.length === 0 ? (
            <div className="border border-zinc-800 p-16 text-center label-mono">Aucune demande reçue</div>
          ) : (
            <div className="border border-zinc-800 divide-y divide-zinc-800">
              {leads.map((ld) => (
                <div key={ld.id} className="p-4" data-testid={`lead-${ld.id}`}>
                  <div className="flex justify-between mb-2">
                    <div className="font-display text-lg">{ld.name}</div>
                    <div className="label-mono">{new Date(ld.created_at).toLocaleDateString("fr-FR")}</div>
                  </div>
                  <div className="label-mono mb-2 text-amber-500">→ {ld.listing_title}</div>
                  <div className="text-sm text-zinc-300 mb-2">{ld.message}</div>
                  <div className="flex gap-3 text-xs text-zinc-500">
                    <a href={`mailto:${ld.email}`}>{ld.email}</a>
                    {ld.phone && <><span>·</span><span>{ld.phone}</span></>}
                    {ld.visit_requested && <><span>·</span><span className="text-amber-500">VISITE DEMANDÉE</span></>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    published: ["PUBLIÉE", "text-emerald-400 border-emerald-500/30"],
    pending: ["EN VALIDATION", "text-amber-500 border-amber-500/30"],
    rejected: ["REFUSÉE", "text-red-400 border-red-500/30"],
    draft: ["BROUILLON", "text-zinc-400 border-zinc-700"],
  };
  const [label, cls] = map[status] || map.draft;
  return <span className={`label-mono border ${cls} px-2 py-1`}>{label}</span>;
}
